const CONTAINER_WSTYPES = new Set(['folder', 'workspace', 'folder_shortcut'])

export type IManageFolderPayload = {
  id: string
  wsid: string
  name: string
  database: string
  path: string
  document_count: number
  wstype?: string
  workspace_id?: string
  workspace_name?: string
  has_subfolders?: boolean
  owner?: string
  owner_description?: string
}

const asRecord = (value: unknown): Record<string, unknown> | null => {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

// A folder/workspace container id is 'LIBRARY!NUM'; a document is 'LIBRARY!NUM.VERSION'.
export const isVersionedDocumentId = (id: string): boolean => {
  const numberPart = id.split('!')[1] ?? id
  return numberPart.includes('.')
}

const payloadFromSelectedItem = (item: Record<string, unknown>): IManageFolderPayload | null => {
  const id = typeof item.id === 'string' ? item.id : null
  if (!id || !id.includes('!') || isVersionedDocumentId(id)) {
    return null
  }
  const wstype = typeof item.wstype === 'string' ? item.wstype : undefined
  if (wstype && !CONTAINER_WSTYPES.has(wstype)) {
    return null
  }
  const parts = id.split('!')
  const database = (typeof item.database === 'string' && item.database) || parts[0] || 'Unknown'
  const wsid = parts[1] || id
  const name = (typeof item.name === 'string' && item.name) || wsid
  const isWorkspace = wstype === 'workspace'
  return {
    id,
    wsid,
    name,
    database,
    path: id,
    document_count: 0,
    wstype,
    workspace_id:
      (typeof item.workspace_id === 'string' && item.workspace_id) ||
      (isWorkspace ? id : undefined),
    workspace_name:
      (typeof item.workspace_name === 'string' && item.workspace_name) ||
      (isWorkspace ? name : undefined),
    has_subfolders: typeof item.has_subfolders === 'boolean' ? item.has_subfolders : undefined,
    owner: typeof item.owner === 'string' ? item.owner : undefined,
    owner_description: typeof item.owner_description === 'string' ? item.owner_description : undefined,
  }
}

// iManage multi-select sends ids as a CSV on `selected` and the real rows on
// `output_data.selected`. Prefer those rows so we keep name and wstype.
export const selectedContainersFromPickerMessage = (data: unknown): IManageFolderPayload[] => {
  const root = asRecord(data)
  if (!root) {
    return []
  }
  const eventData = asRecord(root.data) ?? root
  const outputData = asRecord(eventData.output_data) ?? asRecord(root.output_data)
  const selected = outputData?.selected
  if (!Array.isArray(selected)) {
    return []
  }
  const folders: IManageFolderPayload[] = []
  for (const item of selected) {
    const record = asRecord(item)
    if (!record) {
      continue
    }
    const payload = payloadFromSelectedItem(record)
    if (payload) {
      folders.push(payload)
    }
  }
  return folders
}

export type IManageNestCheckItem = {
  id: string
  name?: string
  wstype?: string
  workspace_id?: string
  ancestor_ids?: string[]
}

export const imanageContainerIdsMatch = (
  left?: string | null,
  right?: string | null,
): boolean => {
  if (!left || !right) {
    return false
  }
  const leftId = left.trim().toLowerCase()
  const rightId = right.trim().toLowerCase()
  if (!leftId || !rightId) {
    return false
  }
  if (leftId === rightId) {
    return true
  }
  if (leftId.includes('!') && rightId.includes('!')) {
    return false
  }
  const leftPart = leftId.includes('!') ? leftId.split('!')[1] : leftId
  const rightPart = rightId.includes('!') ? rightId.split('!')[1] : rightId
  return leftPart === rightPart
}

const lineageOf = (item: IManageNestCheckItem): string[] => {
  if (item.ancestor_ids && item.ancestor_ids.length > 0) {
    return item.ancestor_ids
  }
  if (item.wstype === 'workspace') {
    return [item.id]
  }
  const lineage: string[] = []
  if (item.workspace_id) {
    lineage.push(item.workspace_id)
  }
  if (item.id && !lineage.some((id) => imanageContainerIdsMatch(id, item.id))) {
    lineage.push(item.id)
  }
  return lineage
}

const lineageIsAncestor = (ancestor: string[], descendant: string[]): boolean => {
  if (!ancestor.length || ancestor.length > descendant.length) {
    return false
  }
  return ancestor.every((id, index) => imanageContainerIdsMatch(id, descendant[index]))
}

export const findNestedIManagePicks = (
  items: IManageNestCheckItem[],
): { parent: IManageNestCheckItem; child: IManageNestCheckItem } | null => {
  for (let index = 0; index < items.length; index += 1) {
    for (let other = index + 1; other < items.length; other += 1) {
      const left = lineageOf(items[index])
      const right = lineageOf(items[other])
      if (lineageIsAncestor(left, right)) {
        return { parent: items[index], child: items[other] }
      }
      if (lineageIsAncestor(right, left)) {
        return { parent: items[other], child: items[index] }
      }
    }
  }
  return null
}

export const nestedIManagePicksMessage = (parentName: string, childName: string): string => {
  return `Cannot sync both '${parentName}' and '${childName}' because one is nested inside the other.`
}
